(() => {
  if (window.hotJetStorytellingLoaded) return;
  window.hotJetStorytellingLoaded = true;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const revealItems = (root) => {
    const staggerGroups = root.querySelectorAll('[data-story-stagger]');
    staggerGroups.forEach((group) => {
      Array.from(group.children).forEach((item, index) => {
        item.dataset.storyReveal = '';
        item.style.setProperty('--story-index', index);
      });
    });

    const items = Array.from(root.querySelectorAll('[data-story-reveal]'));
    root.classList.add('story-motion--ready');

    if (prefersReducedMotion.matches || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      root.classList.add('is-active');
      return;
    }

    const itemObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );

    items.forEach((item) => itemObserver.observe(item));

    const rootObserver = new IntersectionObserver(
      (entries, observer) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        root.classList.add('is-active');
        observer.disconnect();
      },
      { threshold: 0.18 }
    );
    rootObserver.observe(root);
  };

  const animateCounter = (counter) => {
    const finalValue = Number(counter.dataset.countTo);
    if (!Number.isFinite(finalValue)) return;

    const suffix = counter.dataset.countSuffix || '';
    const render = (value) => {
      counter.textContent = `${Math.round(value).toLocaleString()}${suffix}`;
    };

    if (prefersReducedMotion.matches || !('IntersectionObserver' in window)) {
      render(finalValue);
      return;
    }

    render(0);
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        const startedAt = performance.now();
        const duration = 1250;

        const tick = (now) => {
          const progress = Math.min((now - startedAt) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          render(finalValue * eased);
          if (progress < 1) requestAnimationFrame(tick);
        };

        requestAnimationFrame(tick);
      },
      { threshold: 0.55 }
    );
    observer.observe(counter);
  };

  class StoryMotion extends HTMLElement {
    connectedCallback() {
      if (this.dataset.storyReady === 'true') return;
      this.dataset.storyReady = 'true';
      revealItems(this);
      this.querySelectorAll('[data-count-to]').forEach(animateCounter);
    }
  }

  class StoryTimeline extends StoryMotion {}

  class JettingComparison extends HTMLElement {
    connectedCallback() {
      if (this.dataset.comparisonReady === 'true') {
        this.observeVisibility();
        return;
      }
      this.dataset.comparisonReady = 'true';

      this.buttons = Array.from(this.querySelectorAll('[data-comparison-mode]'));
      this.panels = Array.from(this.querySelectorAll('[data-comparison-panel]'));
      this.output = this.querySelector('[data-mode-output]');
      this.pauseButton = this.querySelector('[data-comparison-pause]');
      if (this.pauseButton) {
        this.pauseButton.hidden = false;
        this.pauseButton.addEventListener('click', () => {
          const paused = this.dataset.paused !== 'true';
          this.dataset.paused = String(paused);
          this.pauseButton.setAttribute('aria-pressed', String(paused));
          this.pauseButton.textContent = paused ? 'Resume motion' : 'Pause motion';
        });
      }

      this.buttons.forEach((button, index) => {
        button.addEventListener('click', () => this.setMode(button.dataset.comparisonMode));
        button.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let nextIndex = index;
          if (event.key === 'ArrowLeft') nextIndex = (index - 1 + this.buttons.length) % this.buttons.length;
          if (event.key === 'ArrowRight') nextIndex = (index + 1) % this.buttons.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = this.buttons.length - 1;
          this.buttons[nextIndex].focus();
          this.setMode(this.buttons[nextIndex].dataset.comparisonMode);
        });
      });

      this.setMode(this.dataset.mode || 'hot');
      this.observeVisibility();
    }

    observeVisibility() {
      if (!('IntersectionObserver' in window)) {
        this.dataset.inView = 'true';
        return;
      }
      this.visibilityObserver = new IntersectionObserver((entries) => {
        this.dataset.inView = String(entries.some((entry) => entry.isIntersecting));
      }, { threshold: 0.1 });
      this.visibilityObserver.observe(this);
    }

    disconnectedCallback() {
      this.visibilityObserver?.disconnect();
    }

    setMode(mode) {
      this.dataset.mode = mode;
      this.buttons.forEach((button) => {
        const isActive = button.dataset.comparisonMode === mode;
        button.setAttribute('aria-selected', String(isActive));
        button.tabIndex = isActive ? 0 : -1;
      });
      this.panels.forEach((panel) => {
        panel.hidden = panel.dataset.comparisonPanel !== mode;
      });
      if (this.output) this.output.textContent = mode === 'hot' ? 'Hot-water cleaning cycle' : 'Cold-water cleaning pass';
    }
  }

  if (!customElements.get('story-motion')) customElements.define('story-motion', StoryMotion);
  if (!customElements.get('story-timeline')) customElements.define('story-timeline', StoryTimeline);
  if (!customElements.get('jetting-comparison')) customElements.define('jetting-comparison', JettingComparison);
})();
